import React, { useState, useRef, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './WorkSection.css';


const canineHeroImg = 'https://ascentbw.com/Images/CanineCrown/CanineCover.webp';
const windplusImg = 'https://ascentbw.com/Images/Windplus/WindplusCover.webp';
const Mgr = 'https://ascentbw.com/Images/MGR/Mgr4.webp';

const workItems = [
  {
    id: 0,
    title: 'Canine Crown - Premium Pet Nutrition Brand',
    category: 'Branding',
    tags: ['Brand Identity', 'Packaging Design', 'Website Design', 'Social Media'],
    size: 'standard',
    morph: 'bottom-right',
    img: canineHeroImg,
    link: '/works/canine-crown'
  },
  {
    id: 1,
    title: 'MGR - University',
    category: 'Branding',
    tags: ['Prospectus'],
    size: 'standard',
    morph: 'bottom-right',
    img: Mgr,
    link: '/works/mgr'
  },
  {
    id: 2,
    title: 'Windplus – Renewable Energy Platform',
    category: 'Web & Apps',
    tags: ['Corporate Website', 'Renewable Energy', 'Website Design'],
    size: 'standard',
    morph: 'top',
    img: windplusImg,
    link: '/works/windplus'
  }
];

export const TiltCard = ({ item }) => {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Motion values for tracking cursor relative positions on the 4 edges (0 to 1 range)
  const x = useMotionValue(0.5);
  const y = useMotionValue(0.5);

  const xSpring = useSpring(x, { damping: 20, stiffness: 150 });
  const ySpring = useSpring(y, { damping: 20, stiffness: 150 });
  const hoverSpring = useSpring(0, { damping: 20, stiffness: 150 });

  // Springs for 3D tilts (Max 12 degrees tilt)
  const rotateXSpring = useSpring(useTransform(y, [0, 1], [12, -12]), { damping: 25, stiffness: 180 });
  const rotateYSpring = useSpring(useTransform(x, [0, 1], [-12, 12]), { damping: 25, stiffness: 180 });

  // Parallax displacement for inside card contents
  const imgXSpring = useSpring(useTransform(x, [0, 1], [-8, 8]), { damping: 25, stiffness: 180 });
  const imgYSpring = useSpring(useTransform(y, [0, 1], [-8, 8]), { damping: 25, stiffness: 180 });

  const scaleSpring = useSpring(1, { damping: 25, stiffness: 200 });

  // 360-degree liquid morphing clipPath string using quadratic beziers
  const clipPathD = useTransform(
    [xSpring, ySpring, hoverSpring],
    ([mx, my, h]) => {
      // Helper to calculate pushed point away from cursor
      const getPushedPoint = (px, py, maxPush) => {
        if (h === 0) return { x: px, y: py };
        const dx = px - mx;
        const dy = py - my;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.001;
        const influence = 0.75;
        let push = Math.max(0, 1 - dist / influence);
        push = push * push * maxPush * h;
        
        return {
          x: px - (dx / dist) * push,
          y: py - (dy / dist) * push
        };
      };

      const r = 0.05;

      const tl_p1 = getPushedPoint(0, r, 0.08);
      const tl_c  = getPushedPoint(0, 0, 0.12);
      const tl_p2 = getPushedPoint(r, 0, 0.08);

      const t_c   = getPushedPoint(0.5, 0, 0.12);

      const tr_p1 = getPushedPoint(1 - r, 0, 0.08);
      const tr_c  = getPushedPoint(1, 0, 0.12);
      const tr_p2 = getPushedPoint(1, r, 0.08);

      const r_c   = getPushedPoint(1, 0.5, 0.12);

      const br_p1 = getPushedPoint(1, 1 - r, 0.08);
      const br_c  = getPushedPoint(1, 1, 0.12);
      const br_p2 = getPushedPoint(1 - r, 1, 0.08);

      const b_c   = getPushedPoint(0.5, 1, 0.12);

      const bl_p1 = getPushedPoint(r, 1, 0.08);
      const bl_c  = getPushedPoint(0, 1, 0.12);
      const bl_p2 = getPushedPoint(0, 1 - r, 0.08);

      const l_c   = getPushedPoint(0, 0.5, 0.12);

      return `M ${tl_p1.x} ${tl_p1.y} 
              Q ${tl_c.x} ${tl_c.y} ${tl_p2.x} ${tl_p2.y} 
              Q ${t_c.x} ${t_c.y} ${tr_p1.x} ${tr_p1.y} 
              Q ${tr_c.x} ${tr_c.y} ${tr_p2.x} ${tr_p2.y} 
              Q ${r_c.x} ${r_c.y} ${br_p1.x} ${br_p1.y} 
              Q ${br_c.x} ${br_c.y} ${br_p2.x} ${br_p2.y} 
              Q ${b_c.x} ${b_c.y} ${bl_p1.x} ${bl_p1.y} 
              Q ${bl_c.x} ${bl_c.y} ${bl_p2.x} ${bl_p2.y} 
              Q ${l_c.x} ${l_c.y} ${tl_p1.x} ${tl_p1.y} Z`;
    }
  );

  // Radial glint glare background
  const glareBg = useTransform(
    [x, y],
    ([latestX, latestY]) =>
      `radial-gradient(circle at ${latestX * 100}% ${latestY * 100}%, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0) 75%)`
  );

  const handleMouseMove = (e) => {
    if (isMobile || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const px = mouseX / width;
    const py = mouseY / height;
    x.set(px);
    y.set(py);
  };

  const handleMouseEnter = (e) => {
    if (isMobile) return;
    scaleSpring.set(0.95);
    hoverSpring.set(1);
    handleMouseMove(e);
  };

  const handleMouseLeave = () => {
    scaleSpring.set(1);
    hoverSpring.set(0);
    x.set(0.5);
    y.set(0.5);
  };

  return (
    <motion.div
      ref={cardRef}
      className={`work-item ${item.size}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => navigate(item.link || '/works')}
      style={{
        perspective: 1200,
      }}
    >
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
        <defs>
          <clipPath id={`clip-${item.id}`} clipPathUnits="objectBoundingBox">
            <motion.path d={clipPathD} />
          </clipPath>
        </defs>
      </svg>

      <motion.div
        className="work-card-inner"
        style={{
          rotateX: isMobile ? 0 : rotateXSpring,
          rotateY: isMobile ? 0 : rotateYSpring,
          scale: scaleSpring,
          clipPath: isMobile ? 'none' : `url(#clip-${item.id})`,
          transformStyle: 'preserve-3d',
        }}
      >
        <motion.div
          className="work-img-wrapper"
          style={{
            x: isMobile ? 0 : imgXSpring,
            y: isMobile ? 0 : imgYSpring,
            scale: isMobile ? 1 : 1.1,
            transformStyle: 'preserve-3d',
          }}
        >
          <img
            src={item.img}
            alt={item.title}
            className="work-img"
            draggable="false"
          />
        </motion.div>

        {!isMobile && (
          <motion.div
            className="work-card-glare"
            style={{
              background: glareBg,
            }}
          />
        )}
      </motion.div>

      <div className="work-meta">
        <h4 className="work-card-title">{item.title}</h4>
        <div className="work-tags">
          {item.tags.map((tag, idx) => (
            <React.Fragment key={idx}>
              <span className="tag-link">{tag}</span>
              {idx < item.tags.length - 1 && <span className="dot">•</span>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const WorkSection = () => {
  const [filter, setFilter] = useState('All');

  const categories = [
    'All',
    'Branding',
    'Web & Apps',
    'Digital Marketing',
    'Media Production',
    'Print & Signage',
    'Corporate Gifts'
  ];

  const filteredItems = filter === 'All'
    ? workItems
    : workItems.filter(item => item.category === filter);

  return (
    <section className="work-section" id="work">
      <Container>
        <div className="work-header">


          <div className="filter-wrapper">
            {categories.map(cat => (
              <button
                key={cat}
                className={`filter-pill ${filter === cat ? 'active' : ''}`}
                onClick={() => setFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="work-grid">
          <AnimatePresence mode="popLayout">
            {filteredItems.map(item => (
              <motion.div
                layout
                key={item.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                className={`work-grid-item-wrapper ${item.size}`}
              >
                <TiltCard item={item} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </Container>
    </section>
  );
};

export default WorkSection;
